import { ProjectPhoto } from '@/app/api';
import { getCoverVideoMedia, ImageSwiperCoverImage } from '@/app/components/ImageSwiper/ImageSwiper';
import MediaDisplay from '@/app/components/ProjectPageHeaderForAbTest/components/MediaDisplay';
import { useRemainingTimeText } from '@/app/components/ProjectTimeLeft/ProjectTimeLeft';
import { TIME_LEFT_WARNING_THRESHOLD_IN_DAYS } from '@/app/constants';
import { useProjectData } from '@/app/providers/ProjectProvider';
import { PropsWithClassname } from '@/app/types';
import { isImageLandscapeOriented } from '@/app/utils/image';
import classNames from 'classnames';
import { isAfter } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends PropsWithClassname {
  images: ProjectPhoto[];
  coverImage: ImageSwiperCoverImage;
  alt?: string;
  mainVideoSlug?: string;
  endsAt?: Date;
}

export const EndsAtWidget = ({ endsAt, className }: { endsAt: Date; className?: string }) => {
  const { timeRemainingText, daysRemaining } = useRemainingTimeText(endsAt);
  const { t } = useTranslation();

  if (daysRemaining > TIME_LEFT_WARNING_THRESHOLD_IN_DAYS || !isAfter(endsAt, new Date())) {
    return null;
  }

  return (
    <div
      className={classNames(
        'z-[1] bg-red-600 absolute px-[12px] py-[8px] text-white text-[12px] rounded-full bottom-0 uppercase tracking-[2px] font-semibold translate-y-[9px]',
        className,
      )}
    >
      {t('components.ImageCarousel.endsAt', { timeRemainingText })}
    </div>
  );
};

export const ImageCarousel = ({ images, mainVideoSlug, endsAt, className }: Props) => {
  const { project } = useProjectData();
  const scrollRef = useRef<HTMLDivElement>(null);

  const mainPhoto = project.content.mainPhoto;
  const [isReady, setIsReady] = useState(false);

  const imagesWithoutMainPhoto = images.filter((image) => image.id !== mainPhoto?.id);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }

    // Let it render one more tick with scrollLeft = 0
    requestAnimationFrame(() => {
      setIsReady(true);
    });
  }, []); // Runs once on mount

  return (
    <div className={classNames('relative mb-[16px]', className)}>
      {endsAt && <EndsAtWidget endsAt={endsAt} />}
      <div
        ref={scrollRef}
        className={classNames(
          'relative w-[100vw] ml-[-24px] flex flex-row gap-[8px] pb-[8px] snap-x snap-mandatory scroll-smooth',
          isReady ? 'overflow-x-scroll' : 'overflow-hidden',
        )}
      >
        {mainVideoSlug && (
          <div className={'snap-center flex bg-primary-light rounded-[4px] ml-[24px]'}>
            <div className="w-[calc(100vw-48px)] max-h-[200px] flex rounded-[4px] overflow-hidden relative">
              <MediaDisplay {...getCoverVideoMedia(mainVideoSlug)} />
            </div>
          </div>
        )}
        {!mainVideoSlug && mainPhoto && (
          <div className={'snap-center flex bg-primary-light rounded-[4px] ml-[24px]'}>
            <div className="w-[calc(100vw-48px)] max-h-[200px] flex rounded-[4px] overflow-hidden">
              <img src={mainPhoto.sizes.fullCropped.path} alt="cover image" className="h-full w-full object-cover" />
            </div>
          </div>
        )}
        {imagesWithoutMainPhoto.map((image, index) => (
          <div
            key={image.id}
            className={classNames(
              'snap-center flex bg-primary-light rounded-[4px]',
              index === imagesWithoutMainPhoto.length - 1 && 'mr-[24px]',
            )}
          >
            <div className="w-[calc(100vw-48px)] max-h-[200px] flex rounded-[4px] overflow-hidden">
              <img
                src={image.sizes.full.path}
                alt={image.description || ''}
                className={classNames(
                  'h-full w-full',
                  isImageLandscapeOriented(image) ? 'object-cover' : 'object-contain',
                )}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
